/**
 * Small shared pieces used across pages, so every list handles loading, empty
 * and error the same way (Design System §8) and every status reads the same.
 */

import React from 'react';

/** Page-level feedback banner. Success banners fade themselves after 3s. */
export function Banner({ kind = 'success', message, onDismiss }) {
  // Callers pass an inline arrow, so `onDismiss` is a new function on every
  // render. Holding it in a ref keeps the timer keyed to the message alone —
  // otherwise an unrelated re-render restarts the countdown indefinitely.
  const dismissRef = React.useRef(onDismiss);
  dismissRef.current = onDismiss;

  React.useEffect(() => {
    if (kind !== 'success' || !message) return undefined;
    const t = setTimeout(() => {
      if (dismissRef.current) dismissRef.current();
    }, 3000);
    return () => clearTimeout(t);
  }, [kind, message]);

  if (!message) return null;

  return (
    <div className={`banner banner-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span>{message}</span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss">
          &times;
        </button>
      )}
    </div>
  );
}

export function Loading({ label = 'Loading…' }) {
  return <div className="state-loading">{label}</div>;
}

export function Empty({ title, hint }) {
  return (
    <div className="state-empty">
      <span className="state-empty-title">{title}</span>
      {hint && <span>{hint}</span>}
    </div>
  );
}

export function SectionIntro({ children }) {
  return (
    <div className="section-intro">
      <span className="section-intro-label">In this section</span>
      <span className="section-intro-text">{children}</span>
    </div>
  );
}

/**
 * Status pill. Colour AND text carry the meaning, so a colour-blind reader
 * loses nothing (Design System §10).
 */
const STATUS_STYLES = {
  'Pending Review': 'badge-amber',
  Reviewed: 'badge-blue',
  'Ready to Send': 'badge-indigo',
  Sent: 'badge-green',
  'Send Failed': 'badge-red',
};

export function StatusBadge({ status }) {
  return <span className={`badge ${STATUS_STYLES[status] || 'badge-grey'}`}>{status || 'Unknown'}</span>;
}

const FLOW_STYLES = {
  'New Issue': 'badge-blue',
  Renewal: 'badge-indigo',
  Denied: 'badge-red',
};

export function FlowBadge({ flowType }) {
  if (!flowType) return <span className="badge badge-grey">Not decided</span>;
  return <span className={`badge ${FLOW_STYLES[flowType] || 'badge-grey'}`}>{flowType}</span>;
}

export function DetailRow({ label, value, empty = 'Not captured' }) {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className={`detail-value${isEmpty ? ' is-empty' : ''}`}>{isEmpty ? empty : value}</span>
    </div>
  );
}

/** Dates render in South African format throughout. */
export function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-ZA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** Pull a readable message out of an axios error. */
export function errorMessage(err, fallback = 'Something went wrong.') {
  return err?.response?.data?.message || err?.message || fallback;
}

export function Modal({ title, subtitle, children, onClose, wide = false }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal${wide ? ' modal-wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {subtitle && <p className="modal-subtitle">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}
